<!-- src/pages/UserView.vue -->
<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useNotificationStore } from '../stores/notifications'
import { useGroupsStore } from '../stores/groups'
import { useOnboardingStore } from '../stores/onboarding'
import { storeToRefs } from 'pinia'
import { formatDate } from '../utils/dateHelpers'
import BaseButton from '../components/BaseButton.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import TwoFactorManagement from '../components/auth/TwoFactorManagement.vue'
import TwoFactorSetup from '../components/auth/TwoFactorSetup.vue'
import TwoFactorVerification from '../components/auth/TwoFactorVerification.vue'
import NotificationItem from '../components/notifications/NotificationItem.vue'
import MealTypesManager from '../components/preferences/MealTypesManager.vue'
import type { TwoFactorSetupData } from '../components/auth/TwoFactorSetup.vue'
import type { Group } from '../types/group'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// ── Tab state ─────────────────────────────────────────────────────────────────
type Tab = 'account' | 'preferences' | 'groups' | 'notifications'
const activeTab = ref<Tab>('account')

function resolveTab(q: unknown): Tab {
  if (q === 'preferences' || q === 'groups' || q === 'notifications') return q
  return 'account'
}

// Sync tab with ?tab= query param (supports direct links like /user?tab=groups)
watch(() => route.query.tab, (q) => { activeTab.value = resolveTab(q) }, { immediate: true })

// ── Groups tab ────────────────────────────────────────────────────────────────
const groupsStore = useGroupsStore()
const { filteredGroups, isLoading: groupsLoading, error: groupsError } = storeToRefs(groupsStore)

watch(activeTab, (tab) => {
  if (tab === 'groups') groupsStore.fetchGroups()
  if (tab === 'notifications') loadNotifications()
})

function groupMemberCount(g: Group) {
  return g.members?.length ?? 0
}

function isGroupOwner(g: Group) {
  return g.owner_id === authStore.user?.id
}

// ── Onboarding ──────────────────────────────────────────────────────────────
const onboardingStore = useOnboardingStore()
const replayOnboarding = () => {
  router.push('/home').then(() => onboardingStore.replay())
}

// ── Profile ───────────────────────────────────────────────────────────────────
const user = computed(() => authStore.currentUser)

const editingProfile = ref(false)
const profileForm = reactive({
  first_name: '',
  last_name: '',
  username: '',
  email: ''
})

const showPasswordModal = ref(false)
const passwordForm = reactive({
  current_password: '',
  new_password: '',
  confirm_password: ''
})

const showDeleteModal = ref(false)
const deleteForm = reactive({
  password: ''
})

const show2FASetup = ref(false)
const show2FAVerification = ref(false)
const setupData = ref<TwoFactorSetupData | null>(null)
const twoFactorError = ref('')
const twoFactorSuccess = ref('')
const twoFactorLoading = ref(false)

const isLoading = computed(() => authStore.isLoading)
const error = computed(() => authStore.error)

const initializeProfileForm = () => {
  if (user.value) {
    profileForm.first_name = user.value.first_name
    profileForm.last_name = user.value.last_name
    profileForm.username = user.value.username
    profileForm.email = user.value.email
  }
}

const editProfile = () => { initializeProfileForm(); editingProfile.value = true }
const cancelProfileEdit = () => { editingProfile.value = false; initializeProfileForm() }

const saveProfile = async () => {
  try {
    const updateData: { first_name: string; last_name: string; username?: string; email?: string } = {
      first_name: profileForm.first_name,
      last_name: profileForm.last_name
    }
    if (user.value && profileForm.username !== user.value.username) updateData.username = profileForm.username
    if (user.value && profileForm.email !== user.value.email) updateData.email = profileForm.email
    await authStore.updateUserProfile(updateData)
    editingProfile.value = false
  } catch (err) {
    console.error('Profile update failed:', err)
  }
}

const openPasswordModal = () => {
  passwordForm.current_password = ''
  passwordForm.new_password = ''
  passwordForm.confirm_password = ''
  showPasswordModal.value = true
}

const closePasswordModal = () => {
  showPasswordModal.value = false
  passwordForm.current_password = ''
  passwordForm.new_password = ''
  passwordForm.confirm_password = ''
}

const changePassword = async () => {
  if (passwordForm.new_password !== passwordForm.confirm_password) return
  try {
    await authStore.changePassword({
      current_password: passwordForm.current_password,
      new_password: passwordForm.new_password
    })
    closePasswordModal()
    router.push('/login')
  } catch (err) {
    console.error('Password change failed:', err)
  }
}

const openDeleteModal = () => { deleteForm.password = ''; showDeleteModal.value = true }
const closeDeleteModal = () => { showDeleteModal.value = false; deleteForm.password = '' }

const deleteAccount = async () => {
  try {
    await authStore.deleteAccount(deleteForm.password)
    closeDeleteModal()
    router.push('/login')
  } catch (err) {
    console.error('Account deletion failed:', err)
  }
}

const handle2FAEnable = async () => {
  try {
    twoFactorLoading.value = true
    twoFactorError.value = ''
    const response = await authStore.enable2FA()
    if (response) { setupData.value = response; show2FASetup.value = true }
  } catch {
    twoFactorError.value = 'Failed to initiate 2FA setup. Please try again.'
  } finally {
    twoFactorLoading.value = false
  }
}

const handle2FASetupContinue = () => { show2FASetup.value = false; show2FAVerification.value = true }

const handle2FAVerification = async (code: string) => {
  try {
    const success = await authStore.verify2FA(code)
    if (success) {
      show2FAVerification.value = false
      setupData.value = null
      twoFactorSuccess.value = 'Two-factor authentication has been enabled successfully!'
      setTimeout(() => { twoFactorSuccess.value = '' }, 5000)
    }
  } catch {}
}

const handle2FADisable = async (data: { password: string; code: string }) => {
  try {
    twoFactorLoading.value = true
    twoFactorError.value = ''
    const success = await authStore.disable2FA(data.password, data.code)
    if (success) {
      twoFactorSuccess.value = 'Two-factor authentication has been disabled.'
      setTimeout(() => { twoFactorSuccess.value = '' }, 5000)
    }
  } catch {
    twoFactorError.value = 'Failed to disable 2FA. Please check your password and code.'
  } finally {
    twoFactorLoading.value = false
  }
}

const handle2FACancel = () => {
  show2FASetup.value = false
  show2FAVerification.value = false
  setupData.value = null
  twoFactorError.value = ''
}

const handleRegenerateCodes = () => {
  twoFactorSuccess.value = 'Backup code regeneration feature coming soon!'
  setTimeout(() => { twoFactorSuccess.value = '' }, 3000)
}

// ── Notifications ─────────────────────────────────────────────────────────────
const notificationStore = useNotificationStore()
const notifPage = ref(1)
const notifFilter = ref<'all' | 'unread'>('all')

const loadNotifications = () => {
  const isRead = notifFilter.value === 'unread' ? false : undefined
  notificationStore.fetchAllNotifications(notifPage.value, isRead)
  notificationStore.fetchPendingInvitations()
}

const handleClearNotifications = async () => {
  await notificationStore.clearAllNotifications()
}

watch([notifPage, notifFilter], loadNotifications)

onMounted(() => {
  initializeProfileForm()
  authStore.clearError()
})
</script>

<template>
  <div class="page-enter page-container profile-container">

    <!-- ── Page Header ──────────────────────────────────────────────────────── -->
    <div class="profile-header">
      <div>
        <h1 class="font-display display-l page-title">Profile</h1>
        <p class="page-subtitle">Manage your account settings and groups</p>
      </div>
      <div v-if="user" class="account-meta">
        <p class="account-meta-label">Account created</p>
        <p class="account-meta-value">{{ formatDate(user.created_at) }}</p>
      </div>
    </div>

    <!-- ── Tab Bar ───────────────────────────────────────────────────────────── -->
    <div class="tab-bar">
      <button
        @click="activeTab = 'account'"
        class="tab-btn"
        :class="{ active: activeTab === 'account' }">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z" fill="currentColor"/></svg>
        Account
      </button>
      <button
        @click="activeTab = 'preferences'"
        class="tab-btn"
        :class="{ active: activeTab === 'preferences' }">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z" fill="currentColor"/></svg>
        Preferences
      </button>
      <button
        @click="activeTab = 'groups'"
        class="tab-btn"
        :class="{ active: activeTab === 'groups' }">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/></svg>
        Groups
      </button>
      <button
        @click="activeTab = 'notifications'"
        class="tab-btn tab-btn--notif"
        :class="{ active: activeTab === 'notifications' }">
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" fill="currentColor"/></svg>
        Notifications
        <span v-if="notificationStore.unreadCount > 0" class="tab-badge">
          {{ notificationStore.unreadCount > 99 ? '99+' : notificationStore.unreadCount }}
        </span>
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <!--  ACCOUNT TAB                                                           -->
    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'account'" class="space-y-8">

      <!-- Loading State -->
      <div v-if="isLoading && !user" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
        <p class="text-body text-gray-500">Loading your account settings...</p>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <div>
            <h4 class="text-sm font-medium text-red-800">Error</h4>
            <p class="text-sm text-red-700 mt-1">{{ error }}</p>
          </div>
        </div>
      </div>

      <div v-if="user" class="space-y-8">

        <!-- ── Profile Information ─────────────────────────────────────────── -->
        <div class="bg-white border border-gray-200 rounded-lg p-6">
          <div class="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div class="min-w-0">
              <h2 class="text-section-header">Profile Information</h2>
              <p class="text-caption mt-1">Update your personal information and email address</p>
            </div>
            <BaseButton v-if="!editingProfile" variant="secondary" @click="editProfile" :disabled="isLoading" class="flex-shrink-0">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Profile
            </BaseButton>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label for="first_name" class="text-label block mb-2">First Name</label>
              <input id="first_name" v-model="profileForm.first_name" type="text" :disabled="!editingProfile"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50" />
            </div>
            <div>
              <label for="last_name" class="text-label block mb-2">Last Name</label>
              <input id="last_name" v-model="profileForm.last_name" type="text" :disabled="!editingProfile"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50" />
            </div>
            <div>
              <label for="username" class="text-label block mb-2">Username</label>
              <input id="username" v-model="profileForm.username" type="text" :disabled="!editingProfile"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                placeholder="Choose a unique username" />
            </div>
            <div>
              <label for="email" class="text-label block mb-2">Email Address</label>
              <input id="email" v-model="profileForm.email" type="email" :disabled="!editingProfile"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50" />
            </div>
          </div>

          <div v-if="editingProfile" class="flex justify-end mt-6 space-x-3">
            <BaseButton variant="secondary" @click="cancelProfileEdit" :disabled="isLoading">Cancel</BaseButton>
            <BaseButton variant="primary" @click="saveProfile" :loading="isLoading">Save Changes</BaseButton>
          </div>
        </div>

        <!-- ── Security Settings ──────────────────────────────────────────── -->
        <div class="space-y-6">
          <div>
            <h2 class="text-section-header">Security Settings</h2>
            <p class="text-caption mt-1">Manage your password and two-factor authentication</p>
          </div>

          <TwoFactorManagement
            :is2FAEnabled="user.two_factor_enabled"
            :loading="twoFactorLoading"
            :error="twoFactorError"
            :success="twoFactorSuccess"
            @enable="handle2FAEnable"
            @disable="handle2FADisable"
            @regenerate-codes="handleRegenerateCodes"
          />

          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center">
                <div class="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-section-header">Password</h3>
                  <p class="text-caption mt-1">Change your account password</p>
                </div>
              </div>
            </div>
            <p class="text-body text-gray-600 mb-4">
              Keep your account secure by using a strong, unique password. After changing your password, you'll need to sign in again.
            </p>
            <BaseButton variant="secondary" @click="openPasswordModal" :disabled="isLoading">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              Change Password
            </BaseButton>
          </div>
        </div>

        <!-- ── Help & Onboarding ──────────────────────────────────────────── -->
        <div class="bg-white border border-gray-200 rounded-lg p-6">
          <div class="flex items-center justify-between flex-wrap gap-3">
            <div class="min-w-0">
              <h2 class="text-section-header">Help &amp; getting started</h2>
              <p class="text-caption mt-1">Browse the full guide, track your setup checklist, or replay the guided tour.</p>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <BaseButton variant="secondary" @click="replayOnboarding">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Replay tour
              </BaseButton>
              <BaseButton variant="primary" @click="router.push('/help')">
                Open help page
              </BaseButton>
            </div>
          </div>
        </div>

        <!-- ── Danger Zone ────────────────────────────────────────────────── -->
        <div class="bg-red-50 border border-red-200 rounded-lg p-6">
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center">
              <div class="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 class="text-section-header text-red-900">Danger Zone</h3>
                <p class="text-caption text-red-700 mt-1">Irreversible and destructive actions</p>
              </div>
            </div>
          </div>
          <div class="bg-white border border-red-300 rounded-lg p-4">
            <div class="flex items-start justify-between">
              <div>
                <h4 class="text-sm font-medium text-red-900 mb-2">Delete Account</h4>
                <p class="text-sm text-red-700">
                  Once you delete your account, there is no going back. All your data will be permanently removed.
                </p>
              </div>
              <BaseButton variant="secondary" @click="openDeleteModal" :disabled="isLoading"
                class="ml-4 bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700">
                Delete Account
              </BaseButton>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <!--  PREFERENCES TAB                                                       -->
    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <div v-else-if="activeTab === 'preferences'" class="space-y-8">
      <MealTypesManager />
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <!--  GROUPS TAB                                                            -->
    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <div v-else-if="activeTab === 'groups'">

      <!-- Header row -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-section-header">My Groups</h2>
          <p class="text-caption mt-1">Manage and organize your meal planning groups</p>
        </div>
        <BaseButton variant="primary" @click="router.push('/groups/new')">
          <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Create Group
        </BaseButton>
      </div>

      <!-- Loading -->
      <div v-if="groupsLoading" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div v-for="n in 4" :key="n" class="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
          <div class="h-3 bg-gray-100 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 rounded w-2/3"></div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="groupsError" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p class="text-sm text-red-700 mb-3">{{ groupsError }}</p>
        <BaseButton variant="secondary" @click="groupsStore.fetchGroups()">Try again</BaseButton>
      </div>

      <!-- Empty state -->
      <div v-else-if="filteredGroups.length === 0"
        class="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <p class="text-gray-500 font-medium mb-1">No groups yet</p>
        <p class="text-sm text-gray-400 mb-5">Create your first group to start organizing meals with others.</p>
        <BaseButton variant="primary" @click="router.push('/groups/new')">Create your first group</BaseButton>
      </div>

      <!-- Groups grid -->
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div
          v-for="group in filteredGroups"
          :key="group.id"
          @click="router.push(`/groups/${group.id}`)"
          class="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:border-primary-300 hover:shadow-md transition-all duration-200 group/card"
        >
          <!-- Name + badges -->
          <div class="flex items-start justify-between gap-3 mb-3">
            <h3 class="font-semibold text-gray-900 group-hover/card:text-primary-600 transition-colors leading-snug">
              {{ group.name }}
            </h3>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <span v-if="group.is_personal"
                class="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                Personal
              </span>
              <span v-if="isGroupOwner(group)"
                class="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                Owner
              </span>
            </div>
          </div>

          <!-- Description -->
          <p class="text-sm text-gray-500 line-clamp-2 mb-4 min-h-[2.5rem]">
            {{ group.description || 'No description provided.' }}
          </p>

          <!-- Footer stats -->
          <div class="flex items-center gap-4 text-xs text-gray-400 border-t border-gray-100 pt-3">
            <span class="flex items-center gap-1">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ groupMemberCount(group) }} {{ groupMemberCount(group) === 1 ? 'member' : 'members' }}
            </span>
            <span class="flex items-center gap-1">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {{ formatDate(group.created_at) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <!--  NOTIFICATIONS TAB                                                     -->
    <!-- ═══════════════════════════════════════════════════════════════════════ -->
    <div v-else-if="activeTab === 'notifications'">
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">

        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 class="text-section-header">Notifications</h2>
            <p class="text-caption mt-1">Your activity and group updates</p>
          </div>
          <div class="flex items-center gap-2">
            <BaseButton v-if="notificationStore.unreadCount > 0" variant="secondary"
              @click="notificationStore.markAllAsRead()">
              Mark all read
            </BaseButton>
            <BaseButton v-if="notificationStore.allNotifications.length > 0" variant="secondary"
              @click="handleClearNotifications"
              class="text-gray-500 hover:text-red-600 hover:border-red-300">
              <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear all
            </BaseButton>
          </div>
        </div>

        <!-- Filter tabs -->
        <div class="flex border-b border-gray-100 px-6">
          <button @click="notifFilter = 'all'; notifPage = 1"
            class="px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px"
            :class="notifFilter === 'all' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'">
            All
          </button>
          <button @click="notifFilter = 'unread'; notifPage = 1"
            class="px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px"
            :class="notifFilter === 'unread' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'">
            Unread
            <span v-if="notificationStore.unreadCount > 0"
              class="ml-1.5 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
              {{ notificationStore.unreadCount }}
            </span>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="notificationStore.isLoading" class="py-10 text-center text-sm text-gray-400">Loading…</div>

        <!-- Empty -->
        <div v-else-if="notificationStore.allNotifications.length === 0"
          class="py-16 text-center">
          <svg class="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <p class="text-sm text-gray-400">No notifications yet</p>
        </div>

        <!-- List -->
        <div v-else class="divide-y divide-gray-100">
          <NotificationItem v-for="notification in notificationStore.allNotifications" :key="notification.id"
            :notification="notification" :show-actions="true" />
        </div>

        <!-- Pagination -->
        <div v-if="notificationStore.allTotalPages > 1"
          class="flex items-center justify-between px-6 py-3 border-t border-gray-100 text-sm">
          <button :disabled="notifPage <= 1" @click="notifPage--"
            class="px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Previous
          </button>
          <span class="text-gray-500">Page {{ notifPage }} of {{ notificationStore.allTotalPages }}</span>
          <button :disabled="notifPage >= notificationStore.allTotalPages" @click="notifPage++"
            class="px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Next
          </button>
        </div>

      </div>
    </div>

    <!-- ── Modals (always mounted) ────────────────────────────────────────── -->

    <!-- Password Change Modal -->
    <div v-if="showPasswordModal" class="modal-overlay modal-overlay-elevated" @click.self="closePasswordModal">
      <div class="modal-content max-w-md" role="dialog" aria-modal="true" aria-labelledby="password-modal-title">
        <div class="modal-header">
          <div class="flex items-center">
            <div class="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h3 class="text-section-header" id="password-modal-title">Change Password</h3>
          </div>
          <button @click="closePasswordModal" class="modal-close-button" aria-label="Close dialog">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="changePassword" class="space-y-4">
            <div>
              <label for="current_password" class="text-label block mb-2">Current Password</label>
              <input id="current_password" v-model="passwordForm.current_password" type="password"
                placeholder="Enter your current password"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                :disabled="isLoading" required />
            </div>
            <div>
              <label for="new_password" class="text-label block mb-2">New Password</label>
              <input id="new_password" v-model="passwordForm.new_password" type="password"
                placeholder="Enter your new password"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                :disabled="isLoading" required />
            </div>
            <div>
              <label for="confirm_password" class="text-label block mb-2">Confirm New Password</label>
              <input id="confirm_password" v-model="passwordForm.confirm_password" type="password"
                placeholder="Confirm your new password"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                :class="{ 'border-red-300 focus:ring-red-500 focus:border-red-500': passwordForm.new_password && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password }"
                :disabled="isLoading" required />
              <p v-if="passwordForm.new_password && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password"
                class="mt-1 text-sm text-red-600">
                Passwords do not match
              </p>
            </div>
          </form>
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <div>
                <h4 class="text-sm font-medium text-yellow-800">Important</h4>
                <p class="text-sm text-yellow-700 mt-1">
                  After changing your password, you'll be signed out and need to log in again with your new password.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <BaseButton variant="secondary" @click="closePasswordModal" :disabled="isLoading">Cancel</BaseButton>
          <BaseButton variant="primary" @click="changePassword" :loading="isLoading"
            :disabled="!passwordForm.current_password || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password">
            Change Password
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Account Deletion Confirmation -->
    <ConfirmDialog
      :show="showDeleteModal"
      title="Delete Account"
      message="Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data."
      confirmText="Delete Account"
      :loading="isLoading"
      @confirm="deleteAccount"
      @cancel="closeDeleteModal"
    />

    <!-- 2FA Setup Modal -->
    <TwoFactorSetup :show="show2FASetup" :setupData="setupData" :loading="twoFactorLoading"
      @continue="handle2FASetupContinue" @cancel="handle2FACancel" />

    <!-- 2FA Verification Modal -->
    <TwoFactorVerification :show="show2FAVerification" :loading="twoFactorLoading" :error="error || undefined"
      @verify="handle2FAVerification" @cancel="handle2FACancel" />

  </div>
</template>

<style scoped>
.profile-container { max-width: 900px; }

.profile-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
}
.page-title    { letter-spacing: -1px; }
.page-subtitle { font-size: 14px; color: #64748B; margin-top: 6px; }

.account-meta       { text-align: right; }
.account-meta-label { font-size: 11px; font-weight: 700; color: #94A3B8; text-transform: uppercase; letter-spacing: .8px; }
.account-meta-value { font-size: 13px; color: #64748B; margin-top: 2px; }

/* ── Tab bar ── */
.tab-bar { display: flex; gap: 4px; margin-bottom: 32px; }

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all .15s;
  font-family: Inter, sans-serif;
  background: transparent;
  color: #64748B;
  font-weight: 500;
}
.tab-btn.active  { background: #EDF3EC; color: #4F8646; font-weight: 700; }
.tab-btn--notif  { position: relative; }

.tab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #BA1A1A;
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 9999px;
}
</style>
